// sales-page/src/pages/ThankYouPlan.jsx
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import salesApi from '../core/salesApi';
import salesLifetimeCss from './SalesLifetime.css?raw';

const ThankYouPlan = () => {
  const navigate = useNavigate();

  useEffect(() => {
    // Clear email verification from localStorage after successful purchase
    salesApi.clearEmailVerification();
  }, []);

  // Page-scoped CSS: same design system as the main sales page, injected on
  // mount and removed on unmount so it never leaks onto other routes.
  useEffect(() => {
    const styleEl = document.createElement('style');
    styleEl.setAttribute('data-thankyou-plan', 'true');
    styleEl.textContent = salesLifetimeCss;
    document.head.appendChild(styleEl);
    return () => styleEl.remove();
  }, []);

  const steps = [
    {
      n: 1,
      title: 'Check Your Email',
      intro: "Within the next 5-10 minutes, you'll receive an email with:",
      items: ['Your payment receipt', 'Login credentials for TrafikTrak', 'Quick start guide']
    },
    {
      n: 2,
      title: 'Access Your Dashboard',
      intro: 'Once you receive your credentials, you can immediately:',
      items: ['Create your first project', 'Set up custom tracking links', 'Start tracking your campaigns']
    },
    {
      n: 3,
      title: 'Watch Tutorial Videos',
      intro: "We've created step-by-step tutorials covering:",
      items: ['Setting up your first campaign', 'Creating bio pages', 'Reading analytics reports']
    },
    {
      n: 4,
      title: 'Join The Community',
      intro: 'Connect with other TrafikTrak users:',
      items: ['Share best practices', 'Get tips from power users', 'Request new features']
    }
  ];

  return (
    <div className="min-h-screen font-['Arial',_sans-serif]">
      <div className="sales-page-container" style={{ padding: '48px 16px 64px' }}>

        {/* Hero */}
        <div style={{ textAlign: 'center', marginBottom: '28px' }}>
          <div className="checklist-item" style={{ justifyContent: 'center', marginBottom: '14px' }}>
            <span className="check-icon" style={{ fontSize: '30px' }}>&#9989;</span>
          </div>
          <h1 className="sales-main-headline" style={{ marginBottom: '10px' }}>
            &#127881; Welcome To TrafikTrak!
          </h1>
          <p style={{ fontSize: '20px', fontWeight: 700, color: '#111111', marginBottom: '6px' }}>
            Your lifetime access has been activated
          </p>
          <p style={{ fontSize: '16px', color: '#666666' }}>
            Your login credentials are being sent to your email right now &mdash; check your inbox
          </p>
        </div>

        {/* Check your email banner */}
        <div className="doc-boxed-section" style={{ background: '#FFF9E6', borderColor: '#D97706', textAlign: 'center' }}>
          <p style={{ fontSize: '20px', fontWeight: 900, color: '#111111', margin: '0 0 8px' }}>&#128235; Check Your Email Now</p>
          <p style={{ fontSize: '16px', color: '#434343', margin: 0, lineHeight: 1.55 }}>
            Your login credentials and receipt are on their way. Check your inbox &mdash; and your spam folder if you don't see it within 10 minutes.
          </p>
        </div>

        {/* What Happens Next */}
        <div className="sales-section-heading" style={{ marginTop: '44px' }}>What Happens Next?</div>

        <div className="grid md:grid-cols-2 gap-6" style={{ marginTop: '20px' }}>
          {steps.map((step) => (
            <div key={step.n} className="doc-boxed-section" style={{ margin: 0 }}>
              <div style={{ width: '32px', height: '32px', border: '1.5px solid #111111', color: '#111111', borderRadius: '4px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '15px', fontWeight: 800, marginBottom: '14px' }}>
                {step.n}
              </div>
              <h3 style={{ fontSize: '19px', fontWeight: 900, color: '#111111', marginBottom: '10px' }}>{step.title}</h3>
              <p style={{ fontSize: '15px', color: '#434343', marginBottom: '12px' }}>{step.intro}</p>
              <div>
                {step.items.map((item) => (
                  <div key={item} className="checklist-item" style={{ fontSize: '15px' }}>
                    <span className="check-icon">&#9989;</span>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Support */}
        <div className="sales-section-heading">Need Help Getting Started?</div>

        <div className="doc-boxed-section" style={{ textAlign: 'center', maxWidth: '360px', margin: '20px auto 0' }}>
          <h3 style={{ fontSize: '19px', fontWeight: 900, color: '#111111', marginBottom: '8px' }}>Email Support</h3>
          <p style={{ fontSize: '15px', color: '#666666', marginBottom: '10px' }}>We typically respond within 24 hours</p>
          <a href="mailto:support@trafiktrak.com" style={{ color: '#1D4ED8', fontWeight: 700, textDecoration: 'underline' }}>
            support@trafiktrak.com
          </a>
        </div>

        {/* Footer CTA */}
        <div style={{ marginTop: '48px', textAlign: 'center' }}>
          <p style={{ fontSize: '16px', color: '#434343', marginBottom: '12px' }}>
            You can close this page now. Check your email for next steps!
          </p>
          <button
            onClick={() => navigate('/')}
            style={{ background: 'none', border: 'none', color: '#1D4ED8', fontWeight: 700, fontSize: '16px', cursor: 'pointer', fontFamily: 'inherit' }}
          >
            &larr; Return to homepage
          </button>
        </div>
      </div>
    </div>
  );
};

export default ThankYouPlan;
